import 'components/EditableTextarea/style.scss'
import {Chunk, Sentence} from "types";
import cn from "classnames";
import {isNully} from "utils";

interface BuildSentenceProps {
    sentence: Sentence;
    selectedSentences: string[];
    highlightSentences: boolean;
    sentenceIndex: number;
}

const ChunkView: React.FC<{ chunk: Chunk; sentenceIndex: number; chunkIndex: number; }>
    = ({chunk, sentenceIndex, chunkIndex}) => {
    // Plain text chunk: render as-is; whitespace is preserved by parent via CSS
    if (chunk.type === 0) {
        return <>{chunk.text}</>;
    }

    // Split into prefix/core/suffix to avoid highlighting surrounding spaces/punct
    const raw = chunk.text;
    const leadingWS = (raw.match(/^\s+/) || [''])[0];
    const trailingWS = (raw.match(/\s+$/) || [''])[0];
    const core = raw.slice(leadingWS.length, raw.length - trailingWS.length);

    let highlightClass = 'neutral-highlight';

    let importance = chunk.type;
    if (importance === 3) {
        importance = 2;
    }
    // Combine medium and big
    if (importance === 1) {
        highlightClass = 'small-highlight'
    } else if (importance == 2) {
        highlightClass = 'medium-highlight'
        // colorType=3 -> big highlight // deprecated
    }
    return <>
        {leadingWS}
        <span
            className={cn("word__highlight", highlightClass)}
            data-word={core}
            data-score={chunk.score}
            data-importance={importance}
        >
            {core}
        </span>
        {trailingWS}
    </>;
}

const BuildSentence: React.FC<BuildSentenceProps> = (props) => {
    const {sentence, selectedSentences, highlightSentences, sentenceIndex} = props;

    // Stores set the tone (and the score: 0..1 for AI, 0..100 for plagiarism); an untoned sentence is plain text
    let sentenceScore: number = sentence.score;
    if (!sentence.tone) {
        return <>{sentence.text}</>;
    }

    // Always attach tone class; visibility is controlled by CSS toggles on host (#textarea)
    const toneClass = 'tooltip__parent-' + sentence.tone
    const isHumanSegment = sentence.tone === 'human' || sentence.tone === 'human-low'

    const raw = sentence.text!;
    const leadingWS = (raw.match(/^\s+/) || [''])[0];
    const trailingWS = (raw.match(/\s+$/) || [''])[0];
    const core = raw.slice(leadingWS.length, raw.length - trailingWS.length);

    const trimChunk = (chunk: Chunk, idx: number) => {
        if (idx === 0) {
            return {
                ...chunk,
                text: chunk.text.slice(leadingWS.length)
            }
        } else if (idx === sentence.chunks!.length - 1) {
            return {
                ...chunk,
                text: chunk.text.slice(0, chunk.text.length - trailingWS.length)
            }
        } else {
            return chunk
        }
    }
    return <>
        {leadingWS}
        <span
            className={cn("tooltip__parent", toneClass)}
            data-score={sentenceScore}
            data-tone={sentence.tone}
        >
        {!isHumanSegment && sentence.chunks && sentence.chunks.length > 0
            ? sentence.chunks.map((chunk, idx) => (
                <ChunkView key={idx} chunk={trimChunk(chunk, idx)} sentenceIndex={sentenceIndex} chunkIndex={idx}/>
            ))
            : core}
    </span>
        {trailingWS}
    </>
}

interface BuildSegmentsProps {
    sentences: Sentence[];
    selectedSentence: string[];
    highlightSentences: boolean;
}

export const BuildSegments: React.FC<BuildSegmentsProps> = ({sentences, selectedSentence, highlightSentences}) => {
    return <>
        {sentences.map((s, index) => (
            <BuildSentence
                key={index}
                sentence={s}
                selectedSentences={selectedSentence}
                highlightSentences={highlightSentences}
                sentenceIndex={index}
            />
        ))}
    </>;
}
